
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useVerifyMembershipPayment } from "@/hooks/useMembershipPurchase";
import ConsumerHeader from "@/components/ConsumerHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, AlertCircle } from "lucide-react";

const MembershipSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const verifyPayment = useVerifyMembershipPayment();
  const [verificationComplete, setVerificationComplete] = useState(false);

  console.log('MembershipSuccess page loaded');
  console.log('URL search params:', Object.fromEntries(searchParams.entries()));
  console.log('Session ID from URL:', sessionId);

  useEffect(() => {
    if (sessionId && !verificationComplete) {
      console.log('Starting payment verification for session:', sessionId);
      verifyPayment.mutate(sessionId, {
        onSettled: () => {
          setVerificationComplete(true);
        }
      });
    } else if (!sessionId) {
      console.error('No session ID found in URL parameters');
    }
  }, [sessionId, verificationComplete]);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (!sessionId) {
    console.log('Rendering no session ID error state');
    return (
      <div className="min-h-screen bg-gray-50">
        <ConsumerHeader />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-6 w-6 text-red-600" />
                <CardTitle className="text-red-800">Payment Verification Error</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                No session ID found in the URL. This usually means:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-1">
                <li>You accessed this page directly instead of from a payment completion</li>
                <li>There was an issue with the payment redirect</li>
                <li>The payment session has expired</li>
              </ul>
              <p className="text-gray-600 mb-4">
                Please try purchasing the membership again, or contact support if the issue persists.
              </p>
              <div className="flex space-x-4">
                <Button onClick={handleGoHome}>
                  Go to Home
                </Button>
                <Button variant="outline" onClick={() => navigate('/dashboard')}>
                  View Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ConsumerHeader />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              {verifyPayment.isSuccess ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : verifyPayment.isError ? (
                <AlertCircle className="h-6 w-6 text-red-600" />
              ) : (
                <div className="h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              )}
              <CardTitle className={
                verifyPayment.isSuccess ? "text-green-800" : 
                verifyPayment.isError ? "text-red-800" : 
                "text-gray-800"
              }>
                {verifyPayment.isSuccess ? "Membership Activated!" : 
                 verifyPayment.isError ? "Verification Failed" : 
                 "Verifying Payment..."}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {verifyPayment.isPending && (
              <p className="text-gray-600 mb-4">
                Please wait while we verify your payment and activate your membership...
              </p>
            )}
            
            {verifyPayment.isSuccess && (
              <>
                <p className="text-gray-600 mb-4">
                  Thank you for your purchase! Your membership has been successfully activated 
                  and is now available in your dashboard.
                </p>
                <div className="flex space-x-4">
                  <Button onClick={handleGoToDashboard}>
                    View My Memberships
                  </Button>
                  <Button variant="outline" onClick={handleGoHome}>
                    Continue Browsing
                  </Button>
                </div>
              </>
            )}
            
            {verifyPayment.isError && (
              <>
                <p className="text-gray-600 mb-4">
                  There was an issue verifying your payment. Please contact support 
                  with your session ID: {sessionId}
                </p>
                <div className="flex space-x-4">
                  <Button onClick={() => verifyPayment.mutate(sessionId)}>
                    Try Again
                  </Button>
                  <Button variant="outline" onClick={handleGoHome}>
                    Go to Home
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MembershipSuccess;
